import React, { useEffect, useState } from "react";
import { Card, Table, Spinner, Form, Button, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import API from "../services/api";

const EmployeeDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(null);
  const [attendanceToday, setAttendanceToday] = useState(null);
  const [attendanceYesterday, setAttendanceYesterday] = useState(null);
  const [assignedVendors, setAssignedVendors] = useState([]);
  const [assignedSuppliers, setAssignedSuppliers] = useState([]);
  const [assignedRequirements, setAssignedRequirements] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [perms, setPerms] = useState([]);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const navigate = useNavigate();

  useEffect(() => {
    const checkRole = async () => {
      try {
        const res = await API.get("/auth/me");
        const perms = res.data?.employeeRole?.permissions || [];
        const list = Array.isArray(perms) ? perms : [];
        setPerms(list);
        const financePerms = [
          "finance:invoice:create",
          "finance:invoice:approve",
          "finance:expense:create",
          "finance:expense:approve",
          "finance:vendor-bill:upload",
          "finance:vendor-bill:approve",
          "finance:payments:update",
          "finance:payments:approve",
          "finance:payments:release",
          "finance:gst:view",
          "finance:tds:view",
          "finance:reports:view",
          "finance:audit-logs:view",
          "salary:view",
          "salary:process",
          "salary:approve",
        ];
        const isFinance = financePerms.some((p) => list.includes(p));
        if (isFinance) {
          setAllowed(false);
          navigate("/employee/accounts", { replace: true });
          return;
        }
        setAllowed(true);
      } catch (err) {
        console.error("Error checking permissions:", err);
        setAllowed(true);
      }
    };
    checkRole();
  }, [navigate]);

  // ✅ Status Badges
  const getStatusBadge = (status) => {
    switch (status) {
      case "Present":
        return <Badge bg="success">{status}</Badge>;
      case "Half Day":
        return <Badge bg="warning text-dark">{status}</Badge>;
      case "Outside Office":
        return <Badge bg="secondary">{status}</Badge>;
      case "Approved":
        return <Badge bg="success">{status}</Badge>;
      case "Pending":
        return <Badge bg="warning text-dark">{status}</Badge>;
      default:
        return <Badge bg="danger">{status || "Absent"}</Badge>;
    }
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  useEffect(() => {
    if (allowed === null || allowed === false) return;
    const canRequirements = perms.includes("requirements:view");
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // ✅ Today’s Attendance
        const todayRes = await API.get("/attendance/today");
        setAttendanceToday(todayRes.data || { status: "Absent", date: new Date().toISOString() });

        // ✅ Yesterday’s Attendance
        const yRes = await API.get("/attendance/yesterday");
        setAttendanceYesterday(yRes.data || { status: "Absent", date: new Date(Date.now() - 86400000).toISOString() });

        const vendorsRes = await API.get(`/employee/vendors?date=${selectedDate}`);
        setAssignedVendors(Array.isArray(vendorsRes.data) ? vendorsRes.data : []);

        const suppliersRes = await API.get(`/employee/suppliers?date=${selectedDate}`);
        setAssignedSuppliers(Array.isArray(suppliersRes.data) ? suppliersRes.data : []);

        if (canRequirements) {
          const reqRes = await API.get(`/employee/requirements?date=${selectedDate}`);
          setAssignedRequirements(Array.isArray(reqRes.data) ? reqRes.data : []);
        } else {
          setAssignedRequirements([]);
        }

        // ✅ Employee profile
        const empRes = await API.get("/employee/me");
        setEmployee(empRes.data || null);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedDate, allowed, perms]);

  if (allowed === null || loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <Spinner animation="border" variant="success" />
      </div>
    );
  }

  return (
    <div>
      {/* 🔹 Employee Info */}
      {employee && (
        <Card className="shadow-sm mb-4 p-3 bg-light">
          <h5 className="mb-1">{employee.name}</h5>
          <p className="mb-0 text-muted">{employee.email}</p>
          <small className="text-success fw-bold">{employee.role}</small>
        </Card>
      )}

      {/* 🔹 Date Filter */}
      <div className="sticky-top bg-white shadow-sm p-3 mb-4" style={{ top: "0px", zIndex: 1020 }}>
        <Form className="d-flex align-items-center gap-3">
          <Form.Label className="mb-0 fw-bold">Select Date:</Form.Label>
          <Form.Control
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{ maxWidth: "250px" }}
          />
          <Button
            variant="success"
            onClick={() => setSelectedDate(new Date().toISOString().split("T")[0])}
          >
            Today
          </Button>
        </Form>
      </div>

      {/* 🔹 Stats Section */}
      <div className="row mb-4">
        <div className="col-md-3">
          <Card className="shadow-sm text-center p-3">
            <h6>Today’s Attendance</h6>
            <h5>
              {formatDate(attendanceToday?.date)} - {getStatusBadge(attendanceToday?.status)}
            </h5>
          </Card>
        </div>
        <div className="col-md-3">
          <Card className="shadow-sm text-center p-3">
            <h6>Yesterday’s Attendance</h6>
            <h5>
              {formatDate(attendanceYesterday?.date)} - {getStatusBadge(attendanceYesterday?.status)}
            </h5>
          </Card>
        </div>
        {assignedVendors.length > 0 && (
          <div className="col-md-3">
            <Card
              className="shadow-sm text-center p-3 clickable-card"
              onClick={() => navigate("/employee/my-vendors")}
              style={{ cursor: "pointer" }}
            >
              <h6>Total Vendors</h6>
              <h5>{assignedVendors.length}</h5>
              <small className="text-primary">View Details</small>
            </Card>
          </div>
        )}
        {assignedSuppliers.length > 0 && (
          <div className="col-md-3">
            <Card
              className="shadow-sm text-center p-3 clickable-card"
              onClick={() => navigate("/employee/my-suppliers")}
              style={{ cursor: "pointer" }}
            >
              <h6>Total Suppliers</h6>
              <h5>{assignedSuppliers.length}</h5>
              <small className="text-primary">View Details</small>
            </Card>
          </div>
        )}
        {perms.includes("requirements:view") && (
          <div className="col-md-3">
            <Card
              className="shadow-sm text-center p-3 clickable-card"
              onClick={() => navigate("/employee/my-requirements")}
              style={{ cursor: "pointer" }}
            >
              <h6>Total Requirements</h6>
              <h5>{assignedRequirements.length}</h5>
              <small className="text-primary">View Details</small>
            </Card>
          </div>
        )}
      </div>

      {/* 🔹 Vendors Table */}
      {assignedVendors.length > 0 && (
        <Card className="shadow-sm mb-4">
          <Card.Header className="bg-success text-white d-flex justify-content-between">
            <span>Assigned Vendors ({formatDate(selectedDate)})</span>
            <Button size="sm" variant="light" onClick={() => navigate("/employee/my-vendors")}>
              View All →
            </Button>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Vendor Name</th>
                  <th>Contact</th>
                  <th>Location</th>
                </tr>
              </thead>
              <tbody>
                {assignedVendors.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center">
                      No vendors assigned
                    </td>
                  </tr>
                ) : (
                  assignedVendors.map((vendor, index) => (
                    <tr
                      key={vendor._id}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate("/employee/my-vendors")}
                    >
                      <td>{index + 1}</td>
                      <td>{vendor.name}</td>
                      <td>{vendor.contactPerson || "N/A"}</td>
                      <td>{vendor.location || "N/A"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {assignedSuppliers.length > 0 && (
        <Card className="shadow-sm mb-4">
          <Card.Header className="bg-success text-white d-flex justify-content-between">
            <span>Assigned Suppliers ({formatDate(selectedDate)})</span>
            <Button size="sm" variant="light" onClick={() => navigate("/employee/my-suppliers")}>
              View All →
            </Button>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Supplier</th>
                  <th>Contact</th>
                  <th>GST</th>
                </tr>
              </thead>
              <tbody>
                {assignedSuppliers.map((supplier, index) => (
                  <tr
                    key={supplier._id}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate("/employee/my-suppliers")}
                  >
                    <td>{index + 1}</td>
                    <td>{supplier.companyName || supplier.supplierName || supplier.fullName || "N/A"}</td>
                    <td>{supplier.contactMobile || supplier.mobile || "N/A"}</td>
                    <td>{supplier.gstNumber || "N/A"}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}

      {/* 🔹 Requirements Table */}
      {perms.includes("requirements:view") && (
        <Card className="shadow-sm">
          <Card.Header className="bg-success text-white d-flex justify-content-between">
            <span>Assigned Requirements ({formatDate(selectedDate)})</span>
            <Button size="sm" variant="light" onClick={() => navigate("/employee/my-requirements")}>
              View All →
            </Button>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Requirement Title</th>
                  <th>Description</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {assignedRequirements.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center">
                      No requirements assigned
                    </td>
                  </tr>
                ) : (
                  assignedRequirements.map((req, index) => (
                    <tr
                      key={req._id}
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate("/employee/my-requirements")}
                    >
                      <td>{index + 1}</td>
                      <td>{req.title}</td>
                      <td>{req.description}</td>
                      <td>{getStatusBadge(req.status)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default EmployeeDashboard;
